import { useState } from 'react'
import { ScoreHistoryChart } from '../charts/ScoreHistoryChart'
import { ScoreHistoryAreaChart } from '../charts/ScoreHistoryAreaChart'
import { ChartDataPoint } from '@/types'
import { LineChart, AreaChart, Calendar, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { cn } from '@/utils/cn'

interface ScoreHistoryOverviewProps {
  data: ChartDataPoint[]
  className?: string
}

type ChartType = 'line' | 'area'
type TimeRange = '7d' | '30d' | '90d' | '1y'

const TIME_RANGE_OPTIONS = [
  { key: '7d' as TimeRange, label: '7 Days' },
  { key: '30d' as TimeRange, label: '30 Days' },
  { key: '90d' as TimeRange, label: '90 Days' },
  { key: '1y' as TimeRange, label: '1 Year' }
]

export function ScoreHistoryOverview({ data, className }: ScoreHistoryOverviewProps) {
  const [chartType, setChartType] = useState<ChartType>('line')
  const [timeRange, setTimeRange] = useState<TimeRange>('30d')
  const [showBands, setShowBands] = useState(true)
  const [showTrendLine, setShowTrendLine] = useState(true)

  // Filter data based on time range
  const filteredData = filterDataByTimeRange(data, timeRange)

  // Calculate statistics
  const stats = calculateStats(filteredData)

  return (
    <div className={cn(
      'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm',
      className
    )}>
      {/* Header with Controls */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Score History
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Track your TLBH score evolution over time
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Time Range Selector */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              {TIME_RANGE_OPTIONS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setTimeRange(key)}
                  className={cn(
                    'px-3 py-1 rounded-md text-sm font-medium transition-colors',
                    timeRange === key
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Chart Type Toggle */}
            <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setChartType('line')}
                className={cn(
                  'flex items-center gap-2 px-3 py-1 rounded-md text-sm font-medium transition-colors',
                  chartType === 'line'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                )}
                title="Line Chart"
              >
                <LineChart className="w-4 h-4" />
              </button>
              <button
                onClick={() => setChartType('area')}
                className={cn(
                  'flex items-center gap-2 px-3 py-1 rounded-md text-sm font-medium transition-colors',
                  chartType === 'area'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                )}
                title="Area Chart"
              >
                <AreaChart className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Chart Options */}
        <div className="flex flex-wrap items-center gap-4 mt-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showBands}
              onChange={(e) => setShowBands(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Show band lines</span>
          </label>
          
          {chartType === 'line' && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showTrendLine}
                onChange={(e) => setShowTrendLine(e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Show trend line</span>
            </label>
          )}
        </div>
      </div>

      {/* Statistics */}
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {stats.current}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Current</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {stats.highest}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Highest</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {stats.lowest}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Lowest</div>
          </div>
          <div className="text-center">
            <div className={cn(
              'flex items-center justify-center gap-1 text-lg font-semibold',
              stats.trend > 0 ? 'text-green-600' : stats.trend < 0 ? 'text-red-600' : 'text-gray-500'
            )}>
              {stats.trend > 0 ? (
                <TrendingUp className="w-4 h-4" />
              ) : stats.trend < 0 ? (
                <TrendingDown className="w-4 h-4" />
              ) : (
                <Minus className="w-4 h-4" />
              )}
              {stats.trend > 0 ? '+' : ''}{stats.trend.toFixed(1)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Change</div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-6">
        {chartType === 'line' ? (
          <ScoreHistoryChart
            data={filteredData}
            timeRange={timeRange}
            showBands={showBands}
            showTrendLine={showTrendLine}
            height={400}
          />
        ) : (
          <ScoreHistoryAreaChart
            data={filteredData}
            timeRange={timeRange}
            showBands={showBands}
            height={400}
          />
        )}
      </div>
    </div>
  )
}

// Helper functions
function filterDataByTimeRange(data: ChartDataPoint[], timeRange: TimeRange): ChartDataPoint[] {
  const now = new Date()
  const days = {
    '7d': 7,
    '30d': 30,
    '90d': 90,
    '1y': 365
  }[timeRange]

  const cutoffDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)
  
  return data.filter(item => new Date(item.date) >= cutoffDate)
}

function calculateStats(data: ChartDataPoint[]) {
  if (data.length === 0) {
    return { current: 0, highest: 0, lowest: 0, trend: 0 }
  }

  const scores = data.map(d => d.score)
  const current = scores[scores.length - 1]
  const highest = Math.max(...scores)
  const lowest = Math.min(...scores)
  
  // Calculate trend (difference between first and last)
  const trend = data.length > 1 ? current - scores[0] : 0

  return { current, highest, lowest, trend }
}